import { useQuery } from '@tanstack/react-query';
import { RecapitulatifIk } from '../../components/ndf/RecapitulatifIk';
import { ndfApi } from '../../services/ndf-api';
import { Etiquette } from '../../components/ndf/ui/Champ';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { BarreApercu } from '../../components/ndf/ui/BarreApercu';
import { useUser } from '../../context/UserContext';
import { apercuActif } from '../../services/apercu';

/**
 * « Moi » — ce que l'application sait de la personne connectée.
 *
 * Écran volontairement court. Il répond à trois questions qu'un salarié se
 * pose vraiment : qui suis-je pour l'application, à qui partent mes notes de
 * frais, et comment je me déconnecte.
 */
export function NdfMoiPage() {
  const { user, logout } = useUser();
  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'moi', apercuActif()],
    queryFn: () => ndfApi.moi(),
    retry: false,
  });

  return (
    <div className="space-y-4">
      <h1 className="font-titre text-lg font-bold text-nuit">Moi</h1>

      {/* Le récapitulatif vit ICI, et pas dans les dépenses : on ne le cherche
          pas en déclarant, mais en fin d'année, quand on veut savoir où l'on
          en est de son cumul. */}
      <RecapitulatifIk />

      {isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      {data && (
        <section className="space-y-3 rounded-lg border border-nuit/10 bg-white p-4">
          <div>
            <p className="font-titre font-bold text-nuit">
              {data.salarie ? `${data.salarie.prenom} ${data.salarie.nom}` : (data.utilisateur.nom ?? '')}
            </p>
            <p className="text-sm text-nuit/60">{data.utilisateur.email}</p>
          </div>

          <div className="flex flex-wrap gap-1">
            {data.profil && <Etiquette ton="actif">{data.profil}</Etiquette>}
            {data.role && <Etiquette>{data.role}</Etiquette>}
            {data.salarie?.matricule && <Etiquette>Matricule {data.salarie.matricule}</Etiquette>}
          </div>

          {!data.salarie && (
            <p className="rounded-md border border-jaune-fond/60 border-l-4 border-l-jaune-fond p-3 text-sm text-jaune">
              Votre compte n’est rattaché à aucune fiche salarié : vous ne pouvez pas déclarer de
              frais. Signalez-le à votre responsable RH.
            </p>
          )}
        </section>
      )}

      {/* Bac à sable : pré-production ET administrateur.
          Il vit ICI parce que c'est le seul écran présent dans la barre
          d'onglets quel que soit le profil : le testeur doit pouvoir revenir à
          son compte même après avoir endossé quelqu'un sans aucun droit
          d'administration.
          Le rôle vient de la SESSION, donc du compte réel : pendant un aperçu,
          l'administrateur garde ce bloc : c'est par là qu'il ressort, alors
          qu'un collaborateur ne l'a jamais vu. Le serveur, lui, n'honore
          l'en-tête d'aperçu que pour un administrateur : ceci n'est que la
          traduction visible d'une règle déjà tenue ailleurs. */}
      {data?.bac_a_sable && user?.role === 'admin' && (
        <section className="rounded-lg border border-nuit/10 bg-white p-4">
          <h2 className="mb-1 font-titre text-sm font-bold text-nuit">Bac à sable</h2>
          <p className="mb-3 text-sm text-nuit/70">
            Environnement de test : vous pouvez utiliser l’application à la place d’une autre
            personne, pour dérouler un circuit complet sans changer de compte Google.
          </p>
          <BarreApercu />
        </section>
      )}

      <section className="rounded-lg border border-nuit/10 bg-white p-4">
        <h2 className="mb-1 font-titre text-sm font-bold text-nuit">Connexion</h2>
        <p className="mb-3 text-sm text-nuit/70">
          Vous êtes connecté avec votre compte Google {user?.email}. Aucun mot de passe n’est
          nécessaire.
        </p>
        <button
          type="button"
          onClick={logout}
          className="min-h-11 rounded-lg px-4 text-sm font-medium text-corail ring-1 ring-corail-fond"
        >
          Se déconnecter
        </button>
      </section>
    </div>
  );
}
